/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in spokes-diagram.css. */
import { useEffect, useId, useRef, useState, type CSSProperties } from "react"

import { useElementWidth } from "@/hooks/useElementWidth"
import { useIsVisible } from "@/hooks/useIsVisible"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { cardRingClassName } from "@/routes/-components/home-new/homeNewStyles"

import { DiagramTooltip } from "./diagram-tooltip"
import {
  CURRENT_SHA,
  spokesInitialPhase,
  spokesPhases,
  type SpokesPhase,
  type WireBranch,
  type WireGroup,
  type WireState,
} from "./spokes-diagram-data"

import "./spokes-diagram.css"

/* ---------------- timing (ms) ---------------- */

const PHASE_COUNT = spokesPhases.length
const PHASE_DURATION = 4800
const LAST_PHASE_EXTRA_HOLD = 2600
const WIPE_DURATION = 700
const TEXT_DELAY = 550
const HIGHLIGHT_DELAY = 1400
const WIRE_FADE = 300
const STATUS_FADE = 320
const FLOW_PERIOD = 1100
const FLIP_STAGGER = 46
const FLIP_SPIN = 230
const FLIP_TICK = 38
const TYPE_MS = 36
const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"

/** Below this container width the scene switches to its stacked phone layout. */
const STACKED_BELOW = 600
/** The stacked scene stops growing here, so a tablet-width column is not one huge column. */
const STACKED_MAX_SCALE = 0.56

function phaseDuration(index: number) {
  return PHASE_DURATION + (index === PHASE_COUNT - 1 ? LAST_PHASE_EXTRA_HOLD : 0)
}

function clamp01(value: number) {
  return value < 0 ? 0 : value > 1 ? 1 : value
}

/* ---------------- split-flap text: pure functions of elapsed ---------------- */

function typeFrame(text: string, elapsed: number) {
  const count = Math.min(text.length, Math.floor(elapsed / TYPE_MS))
  const shown = text.slice(0, count)
  return count < text.length ? `${shown}▌` : shown
}

function flipFrame(to: string, from: string, elapsed: number) {
  const length = Math.max(to.length, from.length)
  const a = from.padEnd(length, " ")
  const b = to.padEnd(length, " ")
  let out = ""
  for (let index = 0; index < length; index++) {
    const start = index * FLIP_STAGGER
    if (elapsed >= start + FLIP_SPIN) out += b[index]
    else if (elapsed < start) out += a[index]
    else out += GLYPHS[(Math.floor((elapsed - start) / FLIP_TICK) + index * 3) % GLYPHS.length]
  }
  return out.trimEnd()
}

interface FieldFrame {
  text: string
  opacity: number
}

/**
 * A reading that appears types in, one that changes flips, one that goes away
 * fades. Shas never type: they are always present, so they only ever flip.
 */
function fieldFrame(
  to: string | null,
  from: string | null,
  elapsed: number,
  isSha = false,
): FieldFrame {
  if (to === from) return { text: to ?? "", opacity: to ? 1 : 0 }
  const fieldElapsed = Math.max(0, elapsed - TEXT_DELAY)
  if (from === null && to !== null) {
    return { text: isSha ? to : typeFrame(to, fieldElapsed), opacity: 1 }
  }
  if (to === null && from !== null) {
    return { text: from, opacity: 1 - clamp01(elapsed / STATUS_FADE) }
  }
  return {
    text: elapsed < TEXT_DELAY ? (from ?? "") : flipFrame(to ?? "", from ?? "", fieldElapsed),
    opacity: 1,
  }
}

/* ---------------- geometry (scene units) ---------------- */

interface Point {
  x: number
  y: number
}

interface Box {
  left: number
  top: number
  width: number
  height: number
}

interface BoxSet {
  pushChip: Box
  fetchChip: Box
  spokes: Box
  votingSet: Box
  replicas: [Box, Box, Box]
  ghes: Box
}

interface SampledPath {
  d: string
  points: Point[]
  at: (fraction: number) => Point
}

interface Layout {
  kind: "wide" | "stacked"
  width: number
  height: number
  boxes: BoxSet
  votingHeaderHeight: number
  ghesLinesTop: number
  paths: Record<WireBranch, { down: SampledPath; up: SampledPath }>
  /** Fixed label anchors; groups without one label their path's midpoint. */
  labelAnchors: Partial<Record<WireGroup, Point>>
}

const pt = (x: number, y: number): Point => ({ x, y })
const DASHED_BORDER = 2
const CORNER_RADIUS = 16

/** A polyline with rounded corners, plus an arc-length sampler for the flow dots. */
function samplePath(points: Point[]): SampledPath {
  const dense: Point[] = [points[0]!]
  let d = `M ${points[0]!.x} ${points[0]!.y}`

  for (let index = 1; index < points.length - 1; index++) {
    const prev = points[index - 1]!
    const cur = points[index]!
    const next = points[index + 1]!
    const len1 = Math.hypot(cur.x - prev.x, cur.y - prev.y) || 1
    const len2 = Math.hypot(next.x - cur.x, next.y - cur.y) || 1
    const r = Math.min(CORNER_RADIUS, len1 / 2, len2 / 2)
    const p1 = pt(cur.x - ((cur.x - prev.x) / len1) * r, cur.y - ((cur.y - prev.y) / len1) * r)
    const p2 = pt(cur.x + ((next.x - cur.x) / len2) * r, cur.y + ((next.y - cur.y) / len2) * r)
    d += ` L ${p1.x} ${p1.y} Q ${cur.x} ${cur.y} ${p2.x} ${p2.y}`
    dense.push(p1)
    for (let step = 1; step <= 8; step++) {
      const t = step / 8
      const u = 1 - t
      dense.push(
        pt(
          u * u * p1.x + 2 * u * t * cur.x + t * t * p2.x,
          u * u * p1.y + 2 * u * t * cur.y + t * t * p2.y,
        ),
      )
    }
  }

  const last = points[points.length - 1]!
  d += ` L ${last.x} ${last.y}`
  dense.push(last)

  const lengths = [0]
  for (let index = 1; index < dense.length; index++) {
    const a = dense[index - 1]!
    const b = dense[index]!
    lengths.push(lengths[index - 1]! + Math.hypot(b.x - a.x, b.y - a.y))
  }
  const total = lengths[lengths.length - 1]!

  return {
    d,
    points,
    at(fraction) {
      const target = clamp01(fraction) * total
      let index = 1
      while (index < lengths.length - 1 && lengths[index]! < target) index++
      const a = dense[index - 1]!
      const b = dense[index]!
      const span = lengths[index]! - lengths[index - 1]! || 1
      const t = (target - lengths[index - 1]!) / span
      return pt(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t)
    },
  }
}

function buildPaths(
  down: Record<WireBranch, Point[]>,
  up?: Partial<Record<WireBranch, Point[]>>,
): Layout["paths"] {
  const entries = (Object.keys(down) as WireBranch[]).map((branch) => {
    const forward = down[branch]
    const reverse = up?.[branch] ?? forward.slice().reverse()
    return [branch, { down: samplePath(forward), up: samplePath(reverse) }] as const
  })
  return Object.fromEntries(entries) as Layout["paths"]
}

function midpoint(points: Point[]) {
  const index = Math.floor((points.length - 1) / 2)
  const a = points[index]!
  const b = points[Math.min(index + 1, points.length - 1)]!
  return pt((a.x + b.x) / 2, (a.y + b.y) / 2)
}

/* Wide: one 1922 x 1082 artboard, scaled as a whole to the column. */
const wideLayout: Layout = (() => {
  const boxes: BoxSet = {
    pushChip: { left: 735, top: 95, width: 178, height: 64 },
    fetchChip: { left: 1009, top: 95, width: 263, height: 64 },
    spokes: { left: 111.5, top: 298.5, width: 1700, height: 183 },
    votingSet: { left: 113, top: 670, width: 1232, height: 308 },
    replicas: [
      { left: 27.9, top: 97.5, width: 379, height: 183 },
      { left: 426.5, top: 97.5, width: 379, height: 183 },
      { left: 827.5, top: 97.5, width: 379, height: 183 },
    ],
    ghes: { left: 1437.06, top: 670, width: 372.942, height: 308 },
  }
  const pushX = boxes.pushChip.left + boxes.pushChip.width / 2
  const fetchX = boxes.fetchChip.left + boxes.fetchChip.width / 2
  const chipsBottom = boxes.pushChip.top + boxes.pushChip.height
  const spokesTop = boxes.spokes.top
  const spokesBottom = boxes.spokes.top + boxes.spokes.height
  const centerX = boxes.spokes.left + boxes.spokes.width / 2
  const replicaInnerLeft = boxes.votingSet.left + DASHED_BORDER
  const [r0X, r1X, r2X] = boxes.replicas.map((r) => replicaInnerLeft + r.left + r.width / 2) as [
    number,
    number,
    number,
  ]
  const replicaTop = boxes.votingSet.top + DASHED_BORDER + boxes.replicas[0].top
  const pushSpineX = 640
  const spineX = 634
  const barY = 592

  return {
    kind: "wide",
    width: 1922,
    height: 1082,
    boxes,
    votingHeaderHeight: 97.5,
    ghesLinesTop: 147.75,
    paths: buildPaths({
      AB: [pt(pushX, chipsBottom), pt(pushX, 220), pt(pushSpineX, 220), pt(pushSpineX, spokesTop)],
      B: [pt(fetchX, chipsBottom), pt(fetchX, 229), pt(centerX, 229), pt(centerX, spokesTop)],
      C0: [pt(spineX, spokesBottom), pt(spineX, barY), pt(r0X, barY), pt(r0X, replicaTop)],
      C1: [pt(spineX, spokesBottom), pt(spineX, barY), pt(r1X, barY), pt(r1X, replicaTop)],
      C2: [pt(spineX, spokesBottom), pt(spineX, barY), pt(r2X, barY), pt(r2X, replicaTop)],
      D: [pt(centerX, spokesBottom), pt(centerX, barY), pt(r1X, barY), pt(r1X, replicaTop)],
    }),
    labelAnchors: { C: pt(spineX, barY - 71) },
  }
})()

/*
 * Stacked: proxy, voting set and geo replica in one column. Traffic to a
 * replica drops down the left gutter and turns in at its left edge; traffic
 * back to the proxy is its own route up the right gutter, so a reply never
 * doubles back along the line it just watched arrive.
 */
const stackedLayout: Layout = (() => {
  const width = 752
  const boxWidth = 599
  const boxLeft = (width - boxWidth) / 2
  const replicaInset = 28
  const columnLeft = boxLeft + DASHED_BORDER + replicaInset
  const columnWidth = boxWidth - 2 * DASHED_BORDER - 2 * replicaInset
  const chipGap = 40
  const chipsLeft = (width - (178 + chipGap + 263)) / 2
  const chipsTop = 40
  const chipsBottom = chipsTop + 64
  const spokesTop = chipsBottom + 64
  const spokesBottom = spokesTop + 183
  const proxyGap = 56
  const votingTop = spokesBottom + proxyGap
  const headerHeight = 24 + 30
  const replicaHeight = 183
  const replicaGap = 20.5
  const replicaTops = [0, 1, 2].map((i) => headerHeight + 20 + i * (replicaHeight + replicaGap))
  const votingHeight = replicaTops[2]! + replicaHeight + 31
  const ghesTop = votingTop + votingHeight + 40
  const ghesHeight = 256

  const boxes: BoxSet = {
    pushChip: { left: chipsLeft, top: chipsTop, width: 178, height: 64 },
    fetchChip: { left: chipsLeft + 178 + chipGap, top: chipsTop, width: 263, height: 64 },
    spokes: { left: columnLeft, top: spokesTop, width: columnWidth, height: 183 },
    votingSet: { left: boxLeft, top: votingTop, width: boxWidth, height: votingHeight },
    replicas: replicaTops.map((top) => ({
      left: replicaInset,
      top,
      width: columnWidth,
      height: replicaHeight,
    })) as BoxSet["replicas"],
    ghes: { left: boxLeft, top: ghesTop, width: boxWidth, height: ghesHeight },
  }

  const pushX = chipsLeft + 178 / 2
  const fetchX = chipsLeft + 178 + chipGap + 263 / 2
  const proxyX = columnLeft + columnWidth / 2
  const busY = spokesBottom + proxyGap / 2
  const trunkLeft = boxLeft / 2
  const trunkRight = width - boxLeft / 2
  const replicaRight = columnLeft + columnWidth
  const rowY = replicaTops.map((top) => votingTop + DASHED_BORDER + top + replicaHeight / 2)
  const downTo = (y: number) => [
    pt(proxyX, spokesBottom),
    pt(proxyX, busY),
    pt(trunkLeft, busY),
    pt(trunkLeft, y),
    pt(columnLeft, y),
  ]
  const upFrom = (y: number) => [
    pt(replicaRight, y),
    pt(trunkRight, y),
    pt(trunkRight, busY),
    pt(proxyX, busY),
    pt(proxyX, spokesBottom),
  ]

  return {
    kind: "stacked",
    width,
    height: ghesTop + ghesHeight + 40,
    boxes,
    votingHeaderHeight: headerHeight,
    ghesLinesTop: 100,
    paths: buildPaths(
      {
        AB: [pt(pushX, chipsBottom), pt(pushX, spokesTop)],
        B: [pt(fetchX, chipsBottom), pt(fetchX, spokesTop)],
        C0: downTo(rowY[0]!),
        C1: downTo(rowY[1]!),
        C2: downTo(rowY[2]!),
        D: downTo(rowY[1]!),
      },
      { C0: upFrom(rowY[0]!), C1: upFrom(rowY[1]!), C2: upFrom(rowY[2]!), D: upFrom(rowY[1]!) },
    ),
    // D runs past replica 01 to reach 02, so its midpoint would sit on a box;
    // C and D never show together, so both borrow the bus.
    labelAnchors: { C: pt(proxyX, busY), D: pt(proxyX, busY) },
  }
})()

/* ---------------- tooltips ---------------- */

const REPLICA_TIP =
  "A repository replica on local disk. Incoming objects are staged before the resulting repository state is returned to the proxy."

const TIPS = {
  push: {
    title: "Push client",
    body: "The Git client talks to one Spokes proxy; it does not push independently to every replica.",
  },
  fetch: {
    title: "Fetch client",
    body: "This is a different Git client. Its fetch is served through the proxy from an up-to-date replica.",
  },
  spokes: {
    title: "Spokes Proxy",
    body: "The proxy streams the request to fileservers, gathers their outcomes, and returns one result to the client.",
  },
  replicas: [
    REPLICA_TIP,
    REPLICA_TIP,
    "If this replica times out or returns a different resulting state, Spokes withholds it from reads and repairs it.",
  ],
  ghes: {
    title: "GHES Geo",
    body: "GitHub Enterprise geo replicas receive replicated state from the proxy and can serve nearby reads while in sync, but they neither count toward nor block the write quorum.",
  },
}

/* ---------------- step model ---------------- */

const GROUPS: Record<WireGroup, WireBranch[]> = {
  AB: ["AB"],
  B: ["B"],
  C: ["C0", "C1", "C2"],
  D: ["D"],
}

function previousPhase(index: number, cycle: number): SpokesPhase {
  if (index === 0 && cycle === 0) return spokesInitialPhase
  return spokesPhases[(index - 1 + PHASE_COUNT) % PHASE_COUNT]!
}

const fanOf = (wire: WireState) => (wire.only ? wire.only.join(",") : "*")

/** How many steps a group has been continuously lit, so a line drawn once is not redrawn. */
function streakLength(index: number, cycle: number, group: WireGroup) {
  const fan = fanOf(spokesPhases[index]!.wires[group])
  let steps = 0
  let at = index
  let atCycle = cycle
  while (steps < PHASE_COUNT - 1) {
    const prev = previousPhase(at, atCycle).wires[group]
    if (!prev.on || fanOf(prev) !== fan) break
    if (at === 0) atCycle--
    at = (at - 1 + PHASE_COUNT) % PHASE_COUNT
    steps++
  }
  return steps
}

function clipFor(points: Point[], progress: number) {
  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const minX = Math.min(...xs) - 4
  const maxX = Math.max(...xs) + 4
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const height = (maxY - minY) * progress + 4
  const downward = points[points.length - 1]!.y >= points[0]!.y
  return {
    x: minX,
    width: maxX - minX,
    y: downward ? minY - 4 : maxY - (maxY - minY) * progress,
    height,
  }
}

interface BranchFrame {
  branch: WireBranch
  path: SampledPath
  opacity: number
  clip: ReturnType<typeof clipFor>
  arrow: boolean
  dots: Point[]
}

interface LabelFrame {
  group: WireGroup
  text: string
  at: Point
  visible: boolean
}

interface SceneFrame {
  fetchSha: FieldFrame
  fetchShaIsCurrent: boolean
  spokesStatus: FieldFrame
  replicas: { sha: FieldFrame; status: FieldFrame; shaIsCurrent: boolean; active: boolean }[]
  branches: BranchFrame[]
  labels: LabelFrame[]
}

function spokesStatusText(layout: Layout, status: string | null) {
  // The stacked inset is too narrow for the qualifier; keep the count.
  return layout.kind === "stacked" && status
    ? status.replace(/\s*·\s*strict majority$/, "")
    : status
}

function getSceneFrame(
  layout: Layout,
  index: number,
  elapsed: number,
  cycle: number,
  showDots: boolean,
): SceneFrame {
  const phase = spokesPhases[index]!
  const prev = previousPhase(index, cycle)
  const branches: BranchFrame[] = []
  const labels: LabelFrame[] = []

  for (const group of Object.keys(GROUPS) as WireGroup[]) {
    const wire = phase.wires[group]
    const prevWire = prev.wires[group]
    const all = GROUPS[group]
    const pathFor = (branch: WireBranch, dir: WireState["dir"]) =>
      layout.paths[branch][dir === "up" ? "up" : "down"]

    if (!wire.on) {
      if (prevWire.on) {
        const fade = 1 - clamp01(elapsed / WIRE_FADE)
        for (const branch of prevWire.only ?? all) {
          const path = pathFor(branch, prevWire.dir)
          branches.push({
            branch,
            path,
            opacity: fade,
            clip: clipFor(path.points, 1),
            arrow: false,
            dots: [],
          })
        }
        if (prevWire.label) {
          const path = pathFor((prevWire.only ?? all)[0]!, prevWire.dir)
          labels.push({
            group,
            text: prevWire.label,
            at: layout.labelAnchors[group] ?? midpoint(path.points),
            visible: false,
          })
        }
      }
      continue
    }

    const streakElapsed = streakLength(index, cycle, group) * PHASE_DURATION + elapsed
    const wipe = clamp01(streakElapsed / WIPE_DURATION)
    const lit = wire.only ?? all
    for (const branch of lit) {
      const path = pathFor(branch, wire.dir)
      const dots =
        showDots && wipe >= 1
          ? [0, 1].map((dot) => {
              const time = index * PHASE_DURATION + elapsed + (dot * FLOW_PERIOD) / 2
              return path.at((time % FLOW_PERIOD) / FLOW_PERIOD)
            })
          : []
      branches.push({
        branch,
        path,
        opacity: 1,
        clip: clipFor(path.points, wipe),
        arrow: wipe >= 0.97,
        dots,
      })
    }

    const label = wire.label ?? (prevWire.on ? prevWire.label : null)
    if (label) {
      const path = pathFor(lit[0]!, wire.label ? wire.dir : prevWire.dir)
      labels.push({
        group,
        text: label,
        at: layout.labelAnchors[group] ?? midpoint(path.points),
        visible: Boolean(wire.label),
      })
    }
  }

  return {
    fetchSha: fieldFrame(phase.fetchSha, prev.fetchSha, elapsed, true),
    fetchShaIsCurrent: phase.fetchSha === CURRENT_SHA,
    spokesStatus: fieldFrame(
      spokesStatusText(layout, phase.spokesStatus),
      spokesStatusText(layout, prev.spokesStatus),
      elapsed,
    ),
    replicas: phase.replicas.map((replica, i) => {
      const prevReplica = prev.replicas[i]!
      const isNewHighlight = phase.highlight === i && prev.highlight !== i
      return {
        sha: fieldFrame(replica.sha, prevReplica.sha, elapsed, true),
        status: fieldFrame(replica.status, prevReplica.status, elapsed),
        shaIsCurrent: replica.sha === CURRENT_SHA,
        active: phase.highlight === i && (!isNewHighlight || elapsed >= HIGHLIGHT_DELAY),
      }
    }),
    branches,
    labels,
  }
}

/* ---------------- hooks ---------------- */

/* ---------------- scene ---------------- */

function GitHubMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"
      />
    </svg>
  )
}

function boxStyle(box: Box): CSSProperties {
  return { left: box.left, top: box.top, width: box.width, height: box.height }
}

function Sha({ frame, current }: { frame: FieldFrame; current: boolean }) {
  return (
    <span className={`spokes-sha ${current ? "" : "spokes-sha--stale"}`}>{frame.text || " "}</span>
  )
}

function Status({ frame }: { frame: FieldFrame }) {
  return (
    <span className="spokes-status" style={{ opacity: frame.opacity }}>
      {frame.text}
    </span>
  )
}

function SpokesScene({
  layout,
  frame,
  scale,
  idPrefix,
}: {
  layout: Layout
  frame: SceneFrame
  scale: number
  idPrefix: string
}) {
  const { boxes } = layout
  const arrowId = `${idPrefix}-arrow`

  return (
    <div
      className={`spokes-scene spokes-scene--${layout.kind}`}
      style={{ width: layout.width, height: layout.height, transform: `scale(${scale})` }}
    >
      <svg
        className="spokes-wires"
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        aria-hidden="true"
      >
        <defs>
          <marker
            id={arrowId}
            markerWidth="10"
            markerHeight="9"
            refX="8"
            refY="4.5"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path className="spokes-arrowhead" d="M0,0 L9,4.5 L0,9 Z" />
          </marker>
          {frame.branches.map(({ branch, clip }) => (
            <clipPath key={branch} id={`${idPrefix}-clip-${branch}`}>
              <rect x={clip.x} y={clip.y} width={clip.width} height={clip.height} />
            </clipPath>
          ))}
        </defs>
        {frame.branches.map(({ branch, dots }) =>
          dots.map((dot, index) => (
            <circle
              key={`${branch}-${index}`}
              className="spokes-flow-dot"
              cx={dot.x}
              cy={dot.y}
              r="4"
            />
          )),
        )}
        {frame.branches.map(({ branch, path, opacity, arrow }) => (
          <path
            key={branch}
            className="spokes-wire"
            d={path.d}
            clipPath={`url(#${idPrefix}-clip-${branch})`}
            markerEnd={arrow ? `url(#${arrowId})` : undefined}
            style={{ opacity }}
          />
        ))}
      </svg>

      <div
        className="spokes-box spokes-chip"
        style={boxStyle(boxes.pushChip)}
        data-tip-title={TIPS.push.title}
        data-tip={TIPS.push.body}
      >
        push <Sha frame={{ text: CURRENT_SHA, opacity: 1 }} current />
      </div>
      <div
        className="spokes-box spokes-chip"
        style={boxStyle(boxes.fetchChip)}
        data-tip-title={TIPS.fetch.title}
        data-tip={TIPS.fetch.body}
      >
        fetch main <Sha frame={frame.fetchSha} current={frame.fetchShaIsCurrent} />
      </div>

      <div
        className="spokes-box spokes-panel"
        style={boxStyle(boxes.spokes)}
        data-tip-title={TIPS.spokes.title}
        data-tip={TIPS.spokes.body}
      >
        <div className="spokes-panel__header">
          <GitHubMark className="spokes-icon" />
          Spokes Proxy
          <span className="spokes-panel__side">coordinates this push</span>
        </div>
        <div className="spokes-panel__frame">
          <div className="spokes-inset spokes-inset--panel">
            main <Sha frame={{ text: CURRENT_SHA, opacity: 1 }} current />
            <Status frame={frame.spokesStatus} />
          </div>
        </div>
      </div>

      <div className="spokes-box spokes-dashed" style={boxStyle(boxes.votingSet)}>
        <div
          className="spokes-dashed__header"
          style={{ height: layout.votingHeaderHeight }}
          data-align={layout.kind === "stacked" ? "top" : undefined}
        >
          <GitHubMark className="spokes-icon" />
          Repo Voting Set
        </div>
        {boxes.replicas.map((box, index) => {
          const replica = frame.replicas[index]!
          return (
            <div
              key={index}
              className={`spokes-replica ${replica.active ? "spokes-replica--active" : ""}`}
              style={boxStyle(box)}
              data-tip-title={`Replica 0${index + 1} · voting replica`}
              data-tip={TIPS.replicas[index]}
            >
              <div className="spokes-replica__header">Replica 0{index + 1}</div>
              <div className="spokes-replica__frame">
                <div className="spokes-inset spokes-inset--replica">
                  main <Sha frame={replica.sha} current={replica.shaIsCurrent} />
                  <Status frame={replica.status} />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div
        className="spokes-box spokes-dashed"
        style={boxStyle(boxes.ghes)}
        data-tip-title={TIPS.ghes.title}
        data-tip={TIPS.ghes.body}
      >
        <div className="spokes-dashed__header" style={{ height: 97.5 }}>
          <GitHubMark className="spokes-icon" />
          GHES Geo
        </div>
        <div className="spokes-ghes-lines" style={{ top: layout.ghesLinesTop }}>
          <div>read replica</div>
          <div>replication · no vote</div>
          <div>outside write path</div>
        </div>
      </div>

      {frame.labels.map((label) => (
        <div
          key={label.group}
          className={`spokes-wire-label ${label.visible ? "spokes-wire-label--visible" : ""}`}
          style={{ left: label.at.x, top: label.at.y }}
        >
          {label.text}
        </div>
      ))}
    </div>
  )
}

/* ---------------- component ---------------- */

interface Timeline {
  phase: number
  elapsed: number
  /** Completed loops; the first pass opens from the empty initial state. */
  cycle: number
}

const lastPhase = PHASE_COUNT - 1
const settledAt = (phase: number, cycle: number): Timeline => ({
  phase,
  elapsed: phaseDuration(phase),
  cycle,
})

/**
 * GitHub's Spokes replicated storage: a proxy streams a push to a three-replica
 * voting set, commits on a strict majority, then serves a later fetch from a
 * replica it knows is current. The geo read replica sits outside the write
 * path and never votes. Steps play while the figure is on screen; under
 * reduced motion it opens settled on the final step and steps on request.
 */
export function SpokesDiagram() {
  const rootRef = useRef<HTMLElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const previousFrameRef = useRef<number | null>(null)
  const idPrefix = `spokes${useId().replace(/[^a-zA-Z0-9]/g, "")}`
  const prefersReducedMotion = usePrefersReducedMotion()
  const isVisible = useIsVisible(rootRef)
  const width = useElementWidth(stageRef)
  const [timeline, setTimeline] = useState<Timeline>({ phase: 0, elapsed: 0, cycle: 0 })
  const [isPlaying, setIsPlaying] = useState(true)
  const [hasInteracted, setHasInteracted] = useState(false)

  // Reduced motion opens on the settled final step until the reader steps.
  const shown = prefersReducedMotion && !hasInteracted ? settledAt(lastPhase, 1) : timeline
  const animating = isVisible && isPlaying && !prefersReducedMotion

  useEffect(() => {
    if (!animating) {
      previousFrameRef.current = null
      return
    }

    let frame = 0
    const animate = (timestamp: number) => {
      const delta = Math.min(60, timestamp - (previousFrameRef.current ?? timestamp))
      previousFrameRef.current = timestamp
      setTimeline((current) => {
        const elapsed = current.elapsed + delta
        if (elapsed < phaseDuration(current.phase)) return { ...current, elapsed }
        const phase = (current.phase + 1) % PHASE_COUNT
        return { phase, elapsed: 0, cycle: current.cycle + (phase === 0 ? 1 : 0) }
      })
      frame = window.requestAnimationFrame(animate)
    }

    frame = window.requestAnimationFrame(animate)
    return () => {
      window.cancelAnimationFrame(frame)
      previousFrameRef.current = null
    }
  }, [animating])

  const layout = width < STACKED_BELOW ? stackedLayout : wideLayout
  const scale =
    layout.kind === "stacked"
      ? Math.min(width / layout.width, STACKED_MAX_SCALE)
      : width / layout.width
  const frame = getSceneFrame(
    layout,
    shown.phase,
    shown.elapsed,
    shown.cycle,
    !prefersReducedMotion,
  )
  const phase = spokesPhases[shown.phase]!
  const paused = prefersReducedMotion || !isPlaying

  function goToStep(index: number) {
    setHasInteracted(true)
    setIsPlaying(false)
    setTimeline(settledAt(index, Math.max(1, shown.cycle)))
  }

  function skip() {
    const next = (shown.phase + 1) % PHASE_COUNT
    const cycle = shown.cycle + (next === 0 ? 1 : 0)
    setHasInteracted(true)
    setTimeline(paused ? settledAt(next, cycle) : { phase: next, elapsed: 0, cycle })
  }

  return (
    <figure
      ref={rootRef}
      className={`spokes-root not-prose my-10 overflow-hidden rounded-xl font-sans ${cardRingClassName}`}
      aria-label="GitHub Spokes: a push committed across a three-replica voting set, then a fetch served from a current replica"
    >
      <div className="flex h-10 items-center gap-2 border-b border-default px-4 text-xs leading-4">
        <span className="shrink-0 text-muted">GitHub Spokes</span>
        <span className="min-w-0 flex-1 truncate text-right font-semibold text-default">
          At least three copies of each repository
        </span>
      </div>

      <div
        ref={stageRef}
        className="spokes-stage"
        style={{ height: layout.height * scale }}
        aria-hidden="true"
      >
        <div className="spokes-scaler" style={{ width: layout.width * scale }}>
          <SpokesScene layout={layout} frame={frame} scale={scale} idPrefix={idPrefix} />
        </div>
      </div>

      <div className="spokes-segments">
        {spokesPhases.map((step, index) => {
          const fill =
            index < shown.phase
              ? 1
              : index === shown.phase
                ? clamp01(shown.elapsed / phaseDuration(index))
                : 0
          return (
            <button
              key={step.title}
              type="button"
              aria-label={`Go to step ${index + 1}: ${step.title}`}
              aria-current={index === shown.phase ? "step" : undefined}
              onClick={() => goToStep(index)}
            >
              <i>
                <b style={{ width: `${fill * 100}%` }} />
              </i>
            </button>
          )
        })}
      </div>

      <div className="spokes-reader">
        <div className="spokes-reader__meta">
          <span className="flex min-w-0 items-center gap-2 text-xs leading-4">
            <span className="shrink-0 font-mono text-muted tabular-nums">
              {String(shown.phase + 1).padStart(2, "0")} / {String(PHASE_COUNT).padStart(2, "0")}
            </span>
            <span className="min-w-0 truncate font-semibold text-default">
              {spokesPhases[shown.phase]!.title}
            </span>
          </span>
          <div className="flex gap-1">
            {prefersReducedMotion ? null : (
              <button
                type="button"
                className="spokes-control"
                aria-label={isPlaying ? "Pause the animation" : "Play the animation"}
                onClick={() => {
                  setHasInteracted(true)
                  // Resuming from a settled step replays it rather than jumping past it.
                  if (!isPlaying && shown.elapsed >= phaseDuration(shown.phase)) {
                    setTimeline({ ...shown, elapsed: 0 })
                  }
                  setIsPlaying(!isPlaying)
                }}
              >
                {isPlaying ? (
                  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <rect x="3" y="2" width="4" height="12" rx="1" />
                    <rect x="9" y="2" width="4" height="12" rx="1" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M4 2.5v11l9-5.5-9-5.5z" />
                  </svg>
                )}
              </button>
            )}
            <button
              type="button"
              className="spokes-control"
              aria-label="Skip to the next step"
              onClick={skip}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M3 2.5v11l7-5.5-7-5.5z" />
                <rect x="11" y="2" width="2" height="12" rx="0.6" />
              </svg>
            </button>
          </div>
        </div>
        <figcaption className="text-sm leading-5 text-muted">
          {phase.caption}
          <ol className="sr-only">
            {spokesPhases.map((step) => (
              <li key={step.title}>{step.caption}</li>
            ))}
          </ol>
        </figcaption>
      </div>
      <DiagramTooltip rootRef={rootRef} />
    </figure>
  )
}
